<template>
  <div class="card h-100 group-actions-widget">
    <div class="card-header">
      <h4 class="card-title">
        {{ widgetTitle }}
      </h4>
      <div class="d-flex flex-wrap align-items-center my-2 hide-fullscreen">
        <div v-if="numberOfUsersThatHaveResponded >= 1">
          <PulsFormSelect
            v-model="selectedCategory"
            :options="categoryOptions"
          />
        </div>
      </div>
    </div>
    <div class="card-body">
      <div class="d-flex remove-fullscreen mb-10">
        <a
          class="btn btn-secondary btn-round btn-sm me-3 d-flex fs-5 align-items-center no-hover"
          :href="link"
          target="_blank"
        >
          {{ $t('widgetGroupActions.links.guide.title') }}
          <i :class="`bi bi-box-arrow-up-right fs-5 ms-3`"></i>
        </a>
      </div>
      <div v-if="anyActionTypeHasActions">
        <div
          v-for="actionType in Object.keys(actionsDividedByActionType)"
          :key="actionType"
        >
          <ActionList
            v-if="actionsDividedByActionType[actionType].length"
            :id="id"
            :actions="actionsDividedByActionType[actionType]"
            :numberOfUsersThatHaveResponded="numberOfUsersThatHaveResponded"
            :selectedCategory="selectedCategory"
            :categories="categories"
            :isFreeTextAction="actionType === 'otherActions'"
          />
        </div>
      </div>
      <div v-else class="mt-10 text-center">
        {{ $t('widgetGroupActions.noActions') }}
      </div>
    </div>
  </div>
</template>

<script>
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
import { errorTypes } from '@/constants/error-types'
import ActionList from './subcomponents/ActionsList.vue'

export const groupActionsWidgetInformation = () => {
  return {
    requests: {
      [requestTypes.SURVEY_DETAILS]: {},
      [requestTypes.ACTIONS]: {},
      [requestTypes.CATEGORIES]: {},
      [requestTypes.TAGS]: {}
    }
  }
}

export const groupActionsWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: [{ height: 2 }]
      },
      {
        cols: [{ height: 10 }]
      }
    ]
  }
}

const GroupActionsWidget = {
  name: 'GroupActionsWidget',
  mixins: [commonWidgetProps],
  components: {
    ActionList
  },
  props: {
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    actions: {
      type: Object,
      default: () => {}
    },
    categories: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      selectedCategory: null
    }
  },
  mounted() {
    if (!this.anyActionTypeHasActions) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    }
  },
  computed: {
    anyActionTypeHasActions() {
      return Object.keys(this.actionsDividedByActionType).some(
        (actionType) => this.actionsDividedByActionType[actionType].length
      )
    },
    link() {
      return this.$t('widgetGroupActions.links.guide.link')
    },
    numberOfUsersThatHaveResponded() {
      return this.tags.participation?.amount_10 || 0
    },
    excludedCategories() {
      // legacy support for exclude_category
      let excludedCategories = this.data?.excludedCategories || []
      if (this.data && this.data.exclude_category) {
        excludedCategories = excludedCategories.concat(
          this.data.exclude_category
        )
      }
      return excludedCategories
    },
    actionsCopy() {
      return JSON.parse(JSON.stringify(this.actions?.actions))
    },
    actionsDividedByActionType() {
      return {
        actions: this.filterAndSortActions({
          type: 'proposal',
          actions: this.actionsCopy
        }),
        otherActions: this.filterAndSortActions({
          type: 'freetext',
          actions: this.actionsCopy
        })
      }
    },
    widgetTitle() {
      return this.$t('widgetGroupActions.title', {
        groupName: this.selectedGroupTitle
      })
    },
    categoryOptions() {
      return [
        { value: null, title: this.$t('widgetGroupActions.allCategories') }
      ].concat(
        Object.keys(this.categories).map((categoryName) => {
          return {
            value: categoryName,
            title: this.$tr(this.categories[categoryName].messages, 'title')
          }
        })
      )
    }
  },
  methods: {
    actionsWithExcludedCategories({ actions, excludedCategories }) {
      const filteredActions = actions
        .map((action) => {
          action.categories = action.categories.filter(
            (cat) =>
              !Object.keys(cat).find((categoryName) =>
                excludedCategories.includes(categoryName)
              )
          )
          return action
        })
        .filter((action) => action.categories.length)
      return filteredActions
    },
    filterAndSortActions({ type, actions }) {
      let filteredActions = actions.filter((action) => action.type === type)
      if (this.excludedCategories?.length) {
        filteredActions = this.actionsWithExcludedCategories({
          actions: filteredActions,
          excludedCategories: this.excludedCategories
        })
      }
      if (this.selectedCategory) {
        const categoryKey = this.selectedCategory
        filteredActions = filteredActions
          .filter((action) =>
            action.categories.some((category) =>
              category.hasOwnProperty(categoryKey)
            )
          )
          .map((action) => {
            const categoryData = action.categories.find((category) =>
              category.hasOwnProperty(categoryKey)
            )
            return {
              ...action,
              numberOfPeople: categoryData[categoryKey].numberOfActions
            }
          })
      }
      return filteredActions.sort((a, b) => b.numberOfPeople - a.numberOfPeople)
    }
  }
}

export default GroupActionsWidget
</script>

<style lang="scss">
.no-hover:hover {
  color: inherit !important;
}
</style>
